import { createRouter, createWebHistory } from 'vue-router'
import { registerRouteGuards } from './guards'

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      component: () => import('@/layouts/PublicLayout.vue'),
      children: [
        {
          path: '',
          name: 'home',
          component: () => import('@/views/UserSignInView.vue'),
        },
        {
          path: 'signin',
          name: 'signin',
          component: () => import('@/views/UserSignInView.vue'),
        },
        {
          path: 'connect',
          name: 'connect',
          component: () => import('@/views/UserConnectView.vue'),
        },
        // Public legal document page - one combined document ("Condiciones
        // de Uso de TPA" = Terms of Service + Privacy Policy bundled), one
        // concrete route per language. All three share the same
        // LegalDocumentView.vue, parametrized via static route `props`;
        // content lives in content/legal/legalDocuments.ts. Not yet linked
        // from anywhere in the app (footer, modal, etc.) beyond the
        // TermsAndConditionsModal's own document-name link - placement
        // elsewhere (site footer, etc.) is still undecided.
        {
          path: 'es/legal',
          name: 'legal-es',
          component: () => import('@/views/legal/LegalDocumentView.vue'),
          props: { locale: 'es' },
        },
        {
          path: 'en/legal',
          name: 'legal-en',
          component: () => import('@/views/legal/LegalDocumentView.vue'),
          props: { locale: 'en' },
        },
        {
          path: 'fr/legal',
          name: 'legal-fr',
          component: () => import('@/views/legal/LegalDocumentView.vue'),
          props: { locale: 'fr' },
        },
        // Public Cookie Policy - same view, `kind: 'cookies'`. The Spanish URL
        // is /cookies (the address the legal text already cites); /es/cookies
        // is an alias so the three languages follow the same /<lang>/ pattern
        // as the legal pages.
        {
          path: 'cookies',
          alias: 'es/cookies',
          name: 'cookies-es',
          component: () => import('@/views/legal/LegalDocumentView.vue'),
          props: { locale: 'es', kind: 'cookies' },
        },
        {
          path: 'en/cookies',
          name: 'cookies-en',
          component: () => import('@/views/legal/LegalDocumentView.vue'),
          props: { locale: 'en', kind: 'cookies' },
        },
        {
          path: 'fr/cookies',
          name: 'cookies-fr',
          component: () => import('@/views/legal/LegalDocumentView.vue'),
          props: { locale: 'fr', kind: 'cookies' },
        },
      ],
    },
    {
      path: '/app',
      component: () => import('@/layouts/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'dashboard',
          // tpa_admin-only approval dashboard. requiresAdminTPA is now
          // enforced in the route guard (was previously declared-but-ignored
          // on the smartway-admin section).
          meta: { requiresAdminTPA: true, requiresOrganization: true, requiresOrgActivation: true },
          component: () => import('@/views/AdminDashboardView.vue'),
        },
        {
          // tpa_admin-only product catalogue management (Stripe-linked).
          path: 'smartway-admin/products',
          name: 'admin-products',
          meta: { requiresAdminTPA: true, requiresView: 'admin-products' },
          component: () => import('@/views/AdminProductsView.vue'),
        },
        {
          path: 'organizations',
          children: [
            {
              path: '',
              name: 'organizations-list',
              component: () => import('@/views/OrganizationsListView.vue'),
              // List is browsable by tpa_admin and org_leader (the latter
              // read-only - no create/activate controls render for them).
              meta: { requiresAdminTPAorOrgLeader: true },
            },
            {
              path: 'new',
              name: 'create-organization',
              component: () => import('@/views/OrganizationCreationView.vue'),
              // Organization creation is tpa_admin-only.
              meta: { requiresAdminTPA: true },
            },
            {
              path: ':id',
              name: 'organization-profile',
              component: () => import('@/views/OrganizationView.vue'),
              meta: { requiresAdminTPAorOrgLeader: true },
              props: true,
            },
            {
              // Credit pool + purchase view - tpa_admin / org_leader (the same
              // gate the org profile uses); per-org access is re-checked server-side.
              path: ':id/credits',
              name: 'organization-credits',
              component: () => import('@/views/CreditsView.vue'),
              meta: { requiresAdminTPAorOrgLeader: true },
              props: true,
            },
          ],
        },
        {
          // Retired standalone org-health page. The dashboard now lives only
          // as the landing tab of the organization profile (OrganizationView),
          // so old bookmarks land on the organizations list - one click from
          // the same panel - instead of a blank route.
          path: 'organization-health',
          redirect: { name: 'organizations-list' },
        },
        {
          path: 'teams',
          meta: { requiresOrganization: true, requiresOrgActivation: true },
          children: [
            {
              path: '',
              name: 'teams-list',
              component: () => import('@/views/TeamsListView.vue'),
              // Management list - tpa_admin / org_leader only. (Team detail
              // below stays broader: it doubles as a team_leader's landing.)
              meta: { requiresAdminTPAorOrgLeader: true },
            },
            {
              path: 'new',
              name: 'create-team',
              // Team creation is tpa_admin / org_leader.
              meta: { requiresAdminTPAorOrgLeader: true },
              component: () => import('@/views/TeamCreationView.vue'),
            },
            {
              path: ':id',
              name: 'team-profile',
              component: () => import('@/views/TeamView.vue'),
              props: true,
              meta: { requiresTeam: true },
            },
            {
              path: ':teamId/questionnaire',
              name: 'team-questionnaire',
              meta: {
                requiresTeam: true,
                requiresTeamActivation: true,
                requiresAdminTPAorTM: true,
              },
              component: () => import('@/views/QuestionnaireView.vue'),
            },
            {
              path: 'tl-dashboard',
              name: 'tl-dashboard',
              meta: {
                requiresTeam: true,
                requiresTeamActivation: true,
                requiresAdminTPAorTL: true,
              },
              component: () => import('@/views/TeamLeaderDashboardView.vue'),
            },
            {
              path: 'gestionar-equipo',
              name: 'gestionar-equipo',
              meta: {
                requiresTeam: true,
                requiresTeamActivation: true,
                requiresAdminTPAorTL: true,
              },
              component: () => import('@/views/TeamManagementView.vue'),
            },
            {
              path: 'realizar-test',
              name: 'realizar-test',
              meta: {
                requiresTeam: true,
                requiresTeamActivation: true,
              },
              component: () => import('@/views/QuestionnaireFormView.vue'),
            },
            {
              path: 'resultados',
              name: 'resultados',
              meta: {
                requiresTeam: true,
                requiresTeamActivation: true,
                // Team Leader, Admin, OR Scrum Master (read-only results).
                requiresAdminTPAorTLorSM: true,
              },
              // Version-aware: renders the body matching the report the
              // session actually holds. New diagnostics are V13, but reports
              // generated before that upgrade stay V12/V11 forever and would
              // render "not available" under a hardcoded V13 view. The
              // per-version routes below remain for direct access.
              component: () => import('@/views/SessionResultsAutoView.vue'),
            },
            {
              path: 'resultados/test',
              name: 'resultados-test',
              meta: {
                requiresTeam: true,
                requiresTeamActivation: true,
                requiresAdminTPAorTL: true,
              },
              component: () => import('@/views/SessionResultsTestView.vue'),
            },
            {
              // V11 orchestrator-driven DUAL-report pipeline (V8-equivalent
              // on the new architecture). Independent technical + team QA
              // regen loops.
              path: 'resultados/v11',
              name: 'resultados-v11',
              meta: {
                requiresTeam: true,
                requiresTeamActivation: true,
                requiresAdminTPAorTL: true,
              },
              component: () => import('@/views/SessionResultsV11View.vue'),
            },
            //{
            //  // V12 anti-redundancy DUAL-report pipeline (V11-equivalent
            //  // plus a deterministic repetition detector and a
            //  // dedup_rewrite pass). Additive route - V11 remains at
            //  // /resultados/v11 untouched.
            //  path: 'resultados/v12',
            //  name: 'resultados-v12',
            //  meta: {
            //    requiresTeam: true,
            //    requiresTeamActivation: true,
            //    requiresAdminTPAorTL: true,
            //  },
            //  component: () => import('@/views/SessionResultsV12View.vue'),
            //},
            {
              // V13 multilingual DUAL-report pipeline (V12-equivalent
              // generated in English, then translated into the team
              // leader's language). Additive route - V11/V12 untouched.
              path: 'resultados/v13',
              name: 'resultados-v13',
              meta: {
                requiresTeam: true,
                requiresTeamActivation: true,
                requiresAdminTPAorTL: true,
              },
              component: () => import('@/views/SessionResultsV13View.vue'),
            },
            {
              // V14 insight-first pipeline (diagnosis -> insight map ->
              // report, behind a deterministic premium acceptance gate) -
              // the ACTIVE pipeline: the worker writes it when a round
              // closes, and /resultados dispatches to it for new sessions.
              // This direct route stays for pinned access; /resultados still
              // renders whichever version a session actually holds, so
              // pre-V14 reports keep their own view.
              path: 'resultados/v14',
              name: 'resultados-v14',
              meta: {
                requiresTeam: true,
                requiresTeamActivation: true,
                requiresAdminTPAorTL: true,
              },
              component: () => import('@/views/SessionResultsV14View.vue'),
            },
            {
              path: 'resultados/prompt-lab',
              name: 'smartway-admin-prompt-lab',
              component: () => import('@/views/PromptLabView.vue'),
            },
            {
              path: 'consejos-tpa',
              name: 'consejos-tpa',
              meta: {
                requiresTeam: true,
                requiresTeamActivation: true,
                // Developer-only. Gated through the RBAC view association -
                // the same mechanism as Simulation / Prompt Lab / Components
                // Lab - rather than a role flag, so who can reach it is
                // decided in ONE place (developerOnlyViewSlugs in
                // seedViewModules.go) and the sidebar entry disappears with
                // the grant instead of needing a second edit here.
                requiresView: 'consejos-tpa',
              },
              component: () => import('@/views/TpaAdviceView.vue'),
            },
            {
              path: 'evaluacion-de-consejos',
              name: 'evaluacion-de-consejos',
              meta: {
                requiresTeam: true,
                requiresTeamActivation: true,
                requiresTeamLeader: true,
              },
              component: () => import('@/views/FeedbackView.vue'),
            },
            {
              // Historical comparison view - aggregates TPI, HPT axes,
              // perception gap, and the evolutionary bars across every
              // completed session of the active team. RBAC follows the
              // same TL / admin gate the other results views use.
              path: 'historical',
              name: 'historical',
              meta: {
                requiresTeam: true,
                requiresTeamActivation: true,
                requiresAdminTPAorTL: true,
              },
              component: () => import('@/views/HistoricalView.vue'),
            },
          ],
        },
        {
          path: 'users',
          meta: { requiresOrganization: true, requiresOrgActivation: true },
          children: [
            {
              path: '',
              name: 'users-list',
              component: () => import('@/views/smartway-admin/UsersListView.vue'),
            },
          ],
        },
        {
          path: 'smartway-admin',
          children: [
            {
              path: '',
              name: 'admin-dashboard',
              meta: { requiresAdminTPA: true },
              component: () => import('@/views/smartway-admin/DashboardView.vue'),
            },
            {
              path: 'questionnaire-sessions',
              name: 'smartway-admin-questionnaire-sessions-list',
              meta: { requiresAdminTPA: true },
              component: () => import('@/views/smartway-admin/QuestionnaireSessionsListView.vue'),
            },
          ],
        },
        {
          path: 'help',
          name: 'help',
          component: () => import('@/views/HelpView.vue'),
        },
        {
          // Quick-start guide: role manual downloads (organization / team
          // leader / scrum master / team member) linked from the Help Center.
          path: 'help/guides',
          name: 'help-guides',
          component: () => import('@/views/HelpGuidesView.vue'),
        },
        {
          // Team-evolution simulation grounded in TPA frameworks. Does
          // not require a team or session - pure archetype comparison.
          path: 'simulation',
          name: 'simulation',
          meta: {
            requiresView: 'simulation',
          },
          component: () => import('@/views/SimulationView.vue'),
        },
        {
          // Prompt Lab for experimenting with prompts. Does
          // not require a team or session - pure archetype comparison.
          path: 'prompt-lab',
          name: 'prompt-lab',
          meta: {
            requiresView: 'prompt-lab',
          },
          component: () => import('@/views/PromptLabView.vue'),
        },
        {
          // Components Lab - developer-only playground for tweaking the
          // data-dashboard component props in real time. requiresView
          // gates the route via the same RBAC mechanism the other dev
          // tools use (Simulation, Prompt Lab).
          path: 'components-lab',
          name: 'components-lab',
          meta: {
            requiresView: 'components-lab',
          },
          component: () => import('@/views/ComponentsLabView.vue'),
        },
      ],
    },
  ],
})

registerRouteGuards(router)

export default router
